import type { EntityId } from './ids.js';
import type { PrivacyClass } from './privacy.js';
import type { RecordEnvelope } from './records.js';

/**
 * Résumé / professional identity — the owner's living CV. One denormalized
 * record holds the whole résumé (skills, experience, projects, credentials,
 * recognitions) because it is single-owner, read-mostly data. Migrated from
 * v1's Professional* family and projected into a calm Career view.
 */

export interface ResumeSkill {
  readonly name: string;
  readonly category: string | null;
  readonly proficiency: string | null;
}

export interface ResumeExperience {
  readonly organization: string;
  readonly role: string;
  readonly type: string | null;
  readonly location: string | null;
  readonly website: string | null;
  readonly startDate: string | null;
  readonly endDate: string | null;
  readonly isCurrent: boolean;
  readonly summary: string | null;
  readonly achievements: string | null;
}

export interface ResumeProject {
  readonly title: string;
  readonly context: string | null;
  readonly status: string | null;
  readonly stack: string | null;
  readonly outcome: string | null;
  readonly url: string | null;
  readonly repo: string | null;
}

export interface ResumeCredential {
  readonly title: string;
  readonly type: string | null;
  readonly issuer: string | null;
  readonly awardedOn: string | null;
  readonly url: string | null;
}

export interface ResumeRecognition {
  readonly title: string;
  readonly type: string | null;
  readonly organization: string | null;
  readonly on: string | null;
  readonly summary: string | null;
  readonly url: string | null;
}

export interface ResumeContact {
  readonly email: string | null;
  readonly phone: string | null;
  readonly github: string | null;
  readonly linkedin: string | null;
  readonly portfolio: string | null;
}

export interface ResumeProfile extends RecordEnvelope {
  readonly kind: 'resume_profile';
  readonly targetRole: string | null;
  readonly seniority: string | null;
  readonly marketFocus: string | null;
  readonly summary: string | null;
  readonly strengths: string | null;
  readonly gaps: string | null;
  readonly goals: string | null;
  readonly philosophy: string | null;
  readonly lifestyle: string | null;
  readonly contact: ResumeContact;
  readonly skills: readonly ResumeSkill[];
  readonly experience: readonly ResumeExperience[];
  readonly projects: readonly ResumeProject[];
  readonly credentials: readonly ResumeCredential[];
  readonly recognitions: readonly ResumeRecognition[];
  readonly linkedIds: readonly EntityId[];
}

export interface ResumeProfileInput {
  readonly targetRole?: string | null;
  readonly seniority?: string | null;
  readonly marketFocus?: string | null;
  readonly summary?: string | null;
  readonly strengths?: string | null;
  readonly gaps?: string | null;
  readonly goals?: string | null;
  readonly philosophy?: string | null;
  readonly lifestyle?: string | null;
  readonly contact?: Partial<ResumeContact>;
  readonly skills?: readonly ResumeSkill[];
  readonly experience?: readonly ResumeExperience[];
  readonly projects?: readonly ResumeProject[];
  readonly credentials?: readonly ResumeCredential[];
  readonly recognitions?: readonly ResumeRecognition[];
  readonly privacyClass?: PrivacyClass;
}

/** Group skills by category label for a calm, sectioned display. Pure. */
export function skillsByCategory(skills: readonly ResumeSkill[]): readonly { category: string; skills: readonly ResumeSkill[] }[] {
  const map = new Map<string, ResumeSkill[]>();
  for (const s of skills) {
    const key = s.category ?? 'Other';
    const list = map.get(key) ?? [];
    list.push(s);
    map.set(key, list);
  }
  return [...map.entries()].map(([category, list]) => ({ category, skills: list }));
}
